// Create League — shared configuration for both flows.
//
// Public and private leagues are ONE system: same inputs, same steps, same
// validation. Only the copy, the league-link prefix and what happens after
// submit differ, so those are the only things that live in here. If you find
// yourself branching on `variant` inside the wizard UI, it probably belongs in
// this file instead.

// Character limits. The inputs show a live counter bottom-right of each field.
//
// tagline — client Rev 4: "We need to reduce the tagline's character limit a
// little. It runs off the screen. In my attachment, character 45 is g, and you
// can not see it... it seemed like 39 characters for the tagline worked. But
// maybe we need less because of smaller phones? I have a regular iPhone 15."
//
// Yes — less. Measured in Chrome with the real Poppins webfont against the line
// the tagline actually lands on: the preview card's second line, 14px/400, inside
// the row's 32px right gutter and the page's 16px side padding. Usable width and
// what fits on ONE line:
//
//   viewport   usable   mixed case   ALL CAPS
//   393px      329px    45 chars     42 chars     <- the client's iPhone 15
//   375px      311px    43 chars     40 chars
//   360px      296px    41 chars     37 chars     <- smallest common Android
//
// 36 clears the worst realistic case (all caps on a 360px phone) with a character
// to spare, and is comfortable everywhere above it. A character count can only
// ever be an approximation — "WWW" is three times the width of "iii" — so the
// preview line also clamps as a backstop; see PublicLeagueRow.
export const LIMITS = {
  // Client Rev 3: "We need to increase the League Title character cap from 28 to
  // 32". Review & Create already ellipsises the name and the link derived from
  // it (Rev 1), so the four extra characters cannot push either row off screen.
  title: 32,
  tagline: 36,
  description: 150,
};

export const STEPS = [
  { n: 1, label: "Basics" },
  { n: 2, label: "Details" },
  { n: 3, label: "Settings" },
  { n: 4, label: "Review" },
];

// The four league-icon options, in the order the design shows them. Each key
// needs a matching public/league-icons/<key>.svg (that file is rasterised and
// uploaded as the league image on submit), an entry in LeagueIcon.jsx and one in
// VALID_ICON_KEYS in leagueImage.js. Simple placeholders on purpose — better art
// is a round-two decision.
export const ICON_KEYS = ["group", "globe", "chart", "network"];

// Experience level — public leagues only. It sets the tone of the league feed
// rather than gating entry: anyone can still join whichever level is picked. The
// blurb under the choices changes with the selection, so the organiser reads what
// they're actually signing up for.
export const EXPERIENCE_OPTIONS = [
  {
    key: "beginner",
    label: "Beginner",
    blurb: "Newer traders can ask basic questions without feeling out of place",
  },
  {
    key: "intermediate",
    label: "Intermediate",
    blurb: "Members understand orders, charts, and common trading concepts",
  },
  {
    key: "advanced",
    label: "Advanced",
    // Client Rev 1, Screen 2: "Advanced contextual description wraps the one
    // word. Please change the line to read: Expect technical language and
    // market knowledge" — "stronger" is what pushed "knowledge" onto its own
    // line, so it goes.
    blurb: "Expect technical language and market knowledge",
  },
  { key: "all", label: "All levels", blurb: "Everyone is welcome" },
];

export const ASSET_OPTIONS = [
  { key: "stocks", label: "Stocks" },
  { key: "crypto", label: "Crypto" },
  { key: "forex", label: "Forex" },
  { key: "mixed", label: "Mixed" },
];

export const ASSET_LABELS = ASSET_OPTIONS.reduce((acc, o) => {
  acc[o.key] = o.label;
  return acc;
}, {});

export const VARIANTS = {
  public: {
    key: "public",
    gameType: "public",
    title: "Create Public League",
    lede: "Anyone can find it. Anyone can join.",
    // Live host, so the value shown in the input prefix and on Review match the
    // link the user is actually given at the end.
    linkPrefix: "qushi.io/leagues/",
    // Fifth and last screen of the flow — the created-league hand-off.
    createdPath: "/contest/public-league-created",
    // A public league is advertised to strangers, so the copy asks for a hook.
    taglinePlaceholder: "A short hook for your league card",
    descriptionPlaceholder: "Tell people more about your league, your rules, your vibe",
    // Public leagues don't choose art at all — no picker, no upload. The icon
    // below is only what gets rasterised and sent as the league image so every
    // league still HAS one. (Private leagues will get an upload of their own.)
    showIconPicker: false,
    showImageUpload: false,
    // The mobile public screens go straight from title to stepper; the private
    // ones keep their lede. Per design, not preference.
    showLedeOnMobile: false,
    // Sets the tone of the feed. Public only.
    showExperienceLevel: true,
    // What each toggle's supporting line says. The two flows word these
    // differently in the designs.
    // Client Rev 1, Screen 3: "Change late join helper to: 'Members can join
    // after the league starts.'"
    lateJoinsHint: "Members can join after the league starts.",
    // Must be one of ICON_KEYS.
    defaultIcon: "globe",
    // Public leagues don't carry an organiser-funded prize — the coin rewards are
    // what a winner takes home, so that card is theirs and the Prize row on
    // Review reads as those rewards.
    showReward: false,
    showPodiumRewards: true,
    reviewRewardLabel: "Prize",
    // Both flows can carry a funded cash prize — the gate is per USER, not per
    // league type: checkCashUser reads allow_posting_cash_prize_league, which an
    // admin sets on the account. Everyone else never sees the option.
    allowCashPrize: true,
    stepSubtitles: {
      1: "Start with the basics.",
      2: "Choose the dates and tradable assets.",
      3: "Set your public league rules.",
      4: "Review and create your league.",
    },
    submitLabel: "Create League",
  },
  private: {
    key: "private",
    gameType: "private",
    title: "Create Private League",
    // Client Rev 1, private Screen 1: "center the subtitle 'Invite your group.
    // Compete privately.'" — two sentences, as written.
    lede: "Invite your group. Compete privately.",
    linkPrefix: "qushi.io/join/",
    createdPath: "/contest/private-league-created",
    // A private league is a group that already knows itself — the copy talks to
    // the group, not to strangers.
    taglinePlaceholder: "Describe your group in one line",
    descriptionPlaceholder: "Add context for your members",
    // A private league brings its own art — the uploaded image is what its invite
    // page and its cards show. No icon picker.
    showIconPicker: false,
    showImageUpload: true,
    showLedeOnMobile: true,
    showExperienceLevel: false,
    lateJoinsHint: "Members can join after the league starts.",
    // A private league is a group of people you know — the group crest is the
    // sensible default. Must be one of ICON_KEYS.
    defaultIcon: "group",
    // Optional, and labelled as such: the podium badges and coins are the reward
    // every league carries, so anything the organiser adds is extra.
    showReward: true,
    rewardRequired: false,
    rewardTitle: "Add a prize",
    // Client Rev 1, private Screen 3: "the subtitle line wraps with one word.
    // change the subtitle to read - Optional prize you provide."
    rewardDescription: "Optional prize you provide.",
    rewardPlaceholder: "$25 gift card for 1st, extra credit for top 3",
    reviewRewardLabel: "Extra prize",
    showPodiumRewards: true,
    allowCashPrize: true,
    stepSubtitles: {
      1: "Start with the basics.",
      2: "Choose the dates and tradable assets.",
      3: "Set your private league rules.",
      4: "Review and create your league.",
    },
    submitLabel: "Create League",
  },
};

export const slugify = (value) =>
  (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export const emptyForm = (variant) => ({
  title: "",
  tagline: "",
  description: "",
  icon: variant.defaultIcon,
  // Private leagues upload their own art: the File is what gets uploaded on
  // submit; the object URL is only for the preview, and is revoked when replaced.
  imageFile: null,
  imagePreview: "",
  // Public leagues declare the room they're setting up. Beginner is the default
  // because it's the least intimidating starting point, not the most common.
  experienceLevel: "beginner",
  vanityUrl: "",
  vanityUrlTouched: false,
  startDate: "",
  endDate: "",
  assetCategory: "stocks",
  // "all" — every asset in the category is in play, and `assetSymbols` stays
  // empty (that is what the API reads as no restriction). "custom" — the league
  // is limited to the tickers in `assetSymbols`.
  assetMode: "all",
  assetSymbols: [],
  joinAfterStart: true,
  privatePortfolio: true,
  reward: "",
  cashLeague: false,
  cashPrizeAmount: "",
  // How the prize pool above is split across the podium. `cashPrizeAmount` stays
  // the TOTAL and its behaviour is unchanged — these three only say how it is
  // divided, and the Cash Game landing page prints them as TOP PRIZE / 2ND
  // PLACE / 3RD PLACE.
  firstPlaceAmount: "",
  secondPlaceAmount: "",
  thirdPlaceAmount: "",
});

// Which step owns each field, so a Review row can jump straight to it.
export const FIELD_STEP = {
  title: 1,
  tagline: 1,
  description: 1,
  icon: 1,
  imageFile: 1,
  vanityUrl: 1,
  startDate: 2,
  endDate: 2,
  experienceLevel: 2,
  assetCategory: 2,
  assetMode: 2,
  assetSymbols: 2,
  joinAfterStart: 3,
  privatePortfolio: 3,
  reward: 3,
  cashPrizeAmount: 3,
  firstPlaceAmount: 3,
  secondPlaceAmount: 3,
  thirdPlaceAmount: 3,
};

// Every field on the creation screens is mandatory — that's why none of them
// carry an asterisk. The only conditional requirements are the ones behind a
// choice the user made: a custom asset list needs tickers, a cash prize needs an
// amount, and the reward field only exists on the flows that show it.
export const validateStep = (step, form, { vanityTaken, variant }) => {
  const e = {};
  if (step === 1) {
    if (!form.title.trim()) e.title = "League name is required.";
    else if (form.title.length > LIMITS.title)
      e.title = `Keep the name to ${LIMITS.title} characters.`;
    if (!form.tagline.trim()) e.tagline = "Add a short tagline for your league card.";
    else if (form.tagline.length > LIMITS.tagline)
      e.tagline = `Keep the tagline to ${LIMITS.tagline} characters.`;
    if (!form.description.trim()) e.description = "Tell people what your league is about.";
    // Only asked for where the picker is shown; public leagues get their icon
    // from the variant default.
    if (variant.showIconPicker && !form.icon) e.icon = "Pick a league icon.";
    if (variant.showImageUpload && !form.imageFile) {
      e.imageFile = "Upload an image for your league.";
    }
    if (!form.vanityUrl.trim()) e.vanityUrl = "Pick a link for your league.";
    else if (vanityTaken) e.vanityUrl = "That link is taken. Try a variation below.";
  }
  if (step === 2) {
    if (!form.startDate) e.startDate = "Start date is required.";
    if (!form.endDate) e.endDate = "End date is required.";
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      e.endDate = "End date must be after the start date.";
    }
    if (form.assetMode === "custom" && (form.assetSymbols || []).length === 0) {
      e.assetSymbols = "Pick at least one asset, or switch to All.";
    }
  }
  if (step === 3) {
    // The toggles always hold a value, so Settings only has required fields
    // behind a choice: the prize text on the flows that ask for one, and the
    // amount once a cash prize is switched on.
    if (variant.showReward && variant.rewardRequired && !form.reward.trim()) {
      e.reward = "Say what the winner gets.";
    }
    if (variant.allowCashPrize && form.cashLeague) {
      const amount = Number(form.cashPrizeAmount);
      if (!form.cashPrizeAmount || !Number.isFinite(amount) || amount <= 0) {
        e.cashPrizeAmount = "Enter the prize amount.";
      }

      // The podium split. First place is required — a cash game with no top
      // prize has nothing to advertise. Second and third are optional, and a
      // blank one simply drops its row from the landing page.
      const first = Number(form.firstPlaceAmount);
      if (!form.firstPlaceAmount || !Number.isFinite(first) || first <= 0) {
        e.firstPlaceAmount = "Enter the 1st place amount.";
      }
      ["secondPlaceAmount", "thirdPlaceAmount"].forEach((key) => {
        if (form[key] === "" || form[key] === null || form[key] === undefined) return;
        const value = Number(form[key]);
        if (!Number.isFinite(value) || value < 0) {
          e[key] = "Enter a valid amount, or leave it blank.";
        }
      });

      // The client's rule: 1st + 2nd + 3rd must equal the Total Prize Pool, and
      // a game whose podium does not add up cannot be published. Only checked
      // once every individual field is itself valid, so the organiser is not
      // told the sum is wrong while they are still filling the fields in.
      //
      // Compared in whole cents, not as floats: 33.33 + 33.33 + 33.34 is
      // 99.99999999999999 in binary floating point, and rejecting that as "does
      // not match $100" would be a bug the organiser can never fix.
      if (!e.cashPrizeAmount && !e.firstPlaceAmount && !e.secondPlaceAmount && !e.thirdPlaceAmount) {
        const cents = (value) => Math.round((Number(value) || 0) * 100);
        const podium =
          cents(form.firstPlaceAmount) +
          cents(form.secondPlaceAmount) +
          cents(form.thirdPlaceAmount);
        if (podium !== cents(form.cashPrizeAmount)) {
          e.firstPlaceAmount =
            "1st + 2nd + 3rd must add up to the total prize pool.";
        }
      }
    }
  }
  return e;
};
